import { createContext, useContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/hooks/useSocket';
import { WS_EVENTS } from '@gatheredhq/types';
import type { OrderCreatedPayload, OrderStatusChangedPayload, OrderCancelledPayload } from '@gatheredhq/types';

interface OrderNotifications {
  /** New orders that have arrived since the Orders page was last opened. */
  newOrderCount: number;
  /** Acknowledge — called when the owner opens the Orders page. */
  clear: () => void;
}

const OrderNotificationsCtx = createContext<OrderNotifications>({ newOrderCount: 0, clear: () => {} });

/**
 * A single "new orders" badge for the Orders nav tab, fed ENTIRELY by
 * WebSocket — no REST request, ever. Replaced the old sidebar order-count
 * badges (Kitchen/Bar/Cashier/Waiter/Delivery), which each seeded from a
 * full-order-history download of five channels on every page load.
 *
 * Why no seed fetch: WebSocket already syncs order changes live, and a
 * notification counter doesn't need the backlog — it means "orders that
 * arrived since you last looked at the Orders page." So it starts at 0 and
 * counts forward.
 *
 * Robust by construction: we track a Set of order IDs, so an order counts at
 * most once no matter how many status events it emits — the naive
 * +1-on-created / −1-on-any-status-change counter would drift negative
 * (one order fires many status changes).
 *   ORDER_CREATED        → track the id (a new order needs attention)
 *   ORDER_STATUS_CHANGED → untrack (staff has started acting on it)
 *   ORDER_CANCELLED      → untrack (it's gone)
 *   clear()              → untrack all (owner opened the Orders page)
 */
export function OrderNotificationsProvider({ children }: { children: ReactNode }) {
  const { slug: venueSlug } = useParams();
  const { venues } = useAuth();

  const rememberedSlug = venueSlug ?? sessionStorage.getItem('ghq_active_venue') ?? undefined;
  const currentVenue = venues.find(v => v.slug === rememberedSlug) ?? venues[0] ?? null;
  const restaurantId = currentVenue?.id ?? null;

  const { connected, on } = useSocket(restaurantId);
  const idsRef = useRef<Set<string>>(new Set());
  const [newOrderCount, setNewOrderCount] = useState(0);

  const sync = useCallback(() => setNewOrderCount(idsRef.current.size), []);

  const clear = useCallback(() => {
    idsRef.current.clear();
    sync();
  }, [sync]);

  // A different venue starts a fresh notification session.
  useEffect(() => {
    idsRef.current.clear();
    setNewOrderCount(0);
  }, [restaurantId]);

  useEffect(() => {
    if (!connected) return;
    const unsubs = [
      on<OrderCreatedPayload>(WS_EVENTS.ORDER_CREATED, ({ orderId }) => {
        if (!orderId) return;
        idsRef.current.add(orderId);
        sync();
      }),
      on<OrderStatusChangedPayload>(WS_EVENTS.ORDER_STATUS_CHANGED, ({ orderId }) => {
        if (idsRef.current.delete(orderId)) sync();
      }),
      on<OrderCancelledPayload>(WS_EVENTS.ORDER_CANCELLED, ({ orderId }) => {
        if (idsRef.current.delete(orderId)) sync();
      }),
    ];
    return () => { unsubs.forEach(fn => fn()); };
  }, [connected, on, sync]);

  return (
    <OrderNotificationsCtx.Provider value={{ newOrderCount, clear }}>
      {children}
    </OrderNotificationsCtx.Provider>
  );
}

/** New-orders badge count + acknowledge for the Orders tab. */
export function useOrderNotifications(): OrderNotifications {
  return useContext(OrderNotificationsCtx);
}
